import {
  colorBaseNeutral75,
  colorBaseNeutralWhite,
} from '@duxton-web/design-tokens/themes/light';
import { AiOutlined } from '@duxton-web/icons';
import { Button, Divider, Flex, Form, Modal, Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CP_LIST_PRODUCTS, getUCDProject, suggestUCDProjects } from 'omega-rtc/actions/ucd';
import { useCreateSmartCampaign } from 'omega-rtc/components/container/CreateSmartCampaignProvider';

// Import proper types
type CallToActionOption = {
  label: string;
  value: string;
};

type PrimaryLanguageContents = {
  lastVariantNumber: number;
  lastRoundNumber: number;
  selectedControlGroupVariant?: string;
  rounds: Record<string, any>;
  mode: string;
};
import SmartCampaignMetaDataAction from 'omega-rtc/components/form/ucd/SmartCampaignMetaDataAction';
import SmartCampaignMetaDataContents from 'omega-rtc/components/form/ucd/SmartCampaignMetaDataContents';
import SmartCampaignMetaDataContext from 'omega-rtc/components/form/ucd/SmartCampaignMetaDataContext';
import SmartCampaignMetaDataImplicitInputs from 'omega-rtc/components/form/ucd/SmartCampaignMetaDataImplicitInputs';
import SmartCampaignMetadataTime from 'omega-rtc/components/form/ucd/SmartCampaignMetaDataTime';
import SmartCampaignMetaDataUser from 'omega-rtc/components/form/ucd/SmartCampaignMetaDataUser';
import {
  CONTENT_METADATA_OPTION_KEYS,
  COUNTRY_ID_VS_COUNTRY_CODE,
  CREATE_SMART_CAMPAIGN_DEFAULTS,
  INTERNAL_STATUS,
  LANGUAGE_CODE_BASED_ON_COUNTRY_ID,
  OTHERS_OF_CVP,
  PN_IMAGE_KEY,
  PROJECT_ES_FIELDS,
  SMART_CAMPAIGN_CHANNELS,
  SMART_CAMPAIGN_CONTENT_SOURCE_MODES,
  SMART_CAMPAIGN_FORM_KEYS,
  SMART_CAMPAIGN_MAX_IMAGE_SIZE,
  VERTICALS_DISPLAY_NAMES,
} from 'omega-rtc/constants/ucd';
import { CVP_DISPLAY_NAMES } from 'omega-rtc/data/smart-campaign';
import {
  getContentPortalCVPOptionsBasedOnVertical,
  getContentPortalProductDisplayLabel,
  getContentPortalCVPValueAfterProductsLoad,
  getCVPOptionsBasedOnVerticalsAndCountry,
  getCVPValueFromKey,
  getCallToActionOptionsFromArray,
  getFormattedCVPKey,
  getMetaDataForMystiqueCall,
  insertMystiqueMetaDataToFormItems,
  isContentPortalProductsEnabled as getIsContentPortalProductsEnabled,
  isContentPortalCVPOthersSelected,
  isCVPOthersSelected,
  validateSmartCampaignFields,
} from 'omega-rtc/utils/smart-campaign-utils';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import './SmartCampaignContentVariants.scss';

dayjs.extend(isSameOrAfter);
dayjs.extend(duration);

const EMPTY_ARRAY: any[] = [];

type SmartCampaignMetaDataFormProps = {
  configs: any;
  isEditMode: boolean;
};

const getProjectSearchBasePayload = () => ({
  sort: { field: PROJECT_ES_FIELDS.updatedAt, asc: false },
  page: 1,
  pageSize: 10,
  filters: [
    // filters for fetching only draft and active projects
    {
      lhs: PROJECT_ES_FIELDS.status,
      operator: 'in',
      rhs: [INTERNAL_STATUS.draft, INTERNAL_STATUS.active],
    },
  ],
});

const SmartCampaignMetaDataForm = ({
  isEditMode,
  ...props
}: SmartCampaignMetaDataFormProps) => {
  const {
    updateImageData,
    selectedLanguages,
    callToActionOptions,
    isCurrentContentSourceManual,
    isPrimaryLanguageContentsEmpty,
    handleLanguageSelect,
    clearContentVariants,
    isMystiqueCallPending,
    setCallToActionOptions,
    imageDataForEachLanguage,
    primaryLanguageContentsInForm,
    isMystiqueContentEmpty,
    generateManualModeContents,
    generateMystiqueContents,
    setPrimaryLanguageContentsInForm,
    showEditableFormInputs,
    setShowEditableFormInputs,
  } = useCreateSmartCampaign();
  const [
    isProjectSelectionConfirmationOpen,
    setIsProjectSelectionConfirmationOpen,
  ] = useState(false);
  const [isProjectChanged, setIsProjectChanged] = useState(false);
  const [
    isVerticalChangeWarningModalOpen,
    setIsVerticalChangeWarningModalOpen,
  ] = useState(false);
  const [previousVerticalCVPValues, setPreviousVerticalCVPValues] = useState(
    [],
  );

  const lastRoundNumber = useMemo(
    () => primaryLanguageContentsInForm.lastRoundNumber,
    [primaryLanguageContentsInForm],
  );

  const dispatch = useDispatch();
  const projectSuggestions = useSelector(
    (state: any) => state.ucd.projectSuggestions,
  );
  const newlyCreatedCampaign = useSelector(
    (state: any) => state.ucd.newlyCreatedCampaign,
  );
  const userEmail = useSelector(
    (state: any) => _.get(state, 'app.user.email', ''),
  );

  const isProjectAndVerticalSelectorDisabled =
    isEditMode || isMystiqueCallPending || newlyCreatedCampaign;

  useEffect(() => {
    if (!projectSuggestions.length) {
      getProjectList(getProjectSearchBasePayload());
    }
  }, []);

  const onProjectSearch = _.debounce((projectName: string) => {
    if (!projectName) {
      return;
    }

    const requestPayload = getProjectSearchBasePayload();
    requestPayload.sort.field = PROJECT_ES_FIELDS.score;
    requestPayload.filters.push({
      lhs: PROJECT_ES_FIELDS.name,
      operator: 'match',
      rhs: projectName,
    });
    getProjectList(requestPayload);
  }, 1000);

  const form = Form.useFormInstance();
  const [cvpValue, setCVPValue] = useState(
    form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP) || '',
  );
  // if left side is undefined (when duplicating campaign), use the value from the form
  const watchProject = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.PROJECT, form) || form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT);
  const watchPushChannelAction = Form.useWatch(
    SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE,
    form,
  );

  const [projectValues, setProjectValue] = useState({
    currentValue: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT),
    previousValue: '',
  });

  const watchVertical = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.VERTICAL, form);
  const watchChannel = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.CHANNEL);
  const projectView = useSelector((state: any) => state.ucd.projectView);
  const countryID =
    projectView && projectView.project && projectView.project.countryID;
  const countryCode =
    (countryID && COUNTRY_ID_VS_COUNTRY_CODE[countryID]) || '';
  const shouldUseContentPortalProducts = getIsContentPortalProductsEnabled();
  const contentPortalProducts = useSelector(
    (state: any) => _.get(state, `ucd.cpProductsByCountry.${countryCode}.active`, EMPTY_ARRAY),
  );
  const hasLoadedContentPortalProducts = useSelector(
    (state: any) => Boolean(
      countryCode &&
      _.has(state, ['ucd', 'cpProductsByCountry', countryCode, 'active']),
    ),
  );
  const hasContentPortalProductsLoadFailed = useSelector(
    (state: any) => Boolean(
      _.get(state, ['requests', getRequestStateKey(CP_LIST_PRODUCTS), 'isFailure']),
    ),
  );
  const contentPortalCvps = useSelector(
    (state: any) => _.get(state, `ucd.cpCvpsByCountry.${countryCode}.active`, EMPTY_ARRAY),
  );

  const getProjectList = (payload: any) => {
    dispatch(suggestUCDProjects(payload));
  };

  const handlePrimaryLanguageChange = (language: string, allowReGeneration: boolean) => {
    handleLanguageSelect([language], allowReGeneration);
  };

  const handleGenerateClick = async ({ languageToGenerateContents }: { languageToGenerateContents?: string }) => {
    const validationResult = await validateSmartCampaignFields({
      form,
      isMystiqueContentEmpty,
      isCVPOthers,
      isEditableInputs: showEditableFormInputs,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded: hasLoadedContentPortalProducts,
      hasContentPortalProductsLoadFailed,
    });

    if (validationResult.length) {
      return;
    }

    generateMystiqueContents({
      primaryLanguage: languageToGenerateContents || selectedLanguages[0],
      isUserEditModeActiveForMystiqueInputs: showEditableFormInputs,
    });
  };

  useEffect(() => {
    const isProjectViewAlreadyExist =
      projectView && projectView.projectID === watchProject;

    if (watchProject && isProjectViewAlreadyExist) {
      handleProjectViewChange();
    }

    if (
      watchProject &&
      !isProjectViewAlreadyExist &&
      !isProjectSelectionConfirmationOpen
    ) {
      dispatch(getUCDProject(watchProject));
    }

    // if selectedProject is not in project search(projectSuggestions) list.
    const doesSelectedProjectExistInProjectList = projectSuggestions.find(
      (projectSuggestion: any) => projectSuggestion.projectID === watchProject,
    );
    if (watchProject && !doesSelectedProjectExistInProjectList) {
      const requestPayload = getProjectSearchBasePayload();
      requestPayload.filters.push({
        lhs: PROJECT_ES_FIELDS.id,
        operator: 'eq',
        rhs: watchProject,
      });
      dispatch(suggestUCDProjects(requestPayload));
    }
  }, [watchProject]);

  useEffect(() => {
    if (
      !projectView ||
      !projectView.projectID ===
      form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT)
    ) {
      return;
    }
    handleProjectViewChange();
    form.validateFields([SMART_CAMPAIGN_FORM_KEYS.PROJECT]);
  }, [projectView]);

  const handleProjectViewChange = () => {
    /**
     * 'isProjectChanged' is used to conform whether project is changed.
     *  This useEffect will trigger on initial render.
     *  In that case we don't need to set primary language (at that time 'isProjectChanged' will be 'false')
     */
    if (isProjectChanged) {
      const countryId = projectView.project.countryID;
      const defaultLanguageBasedOnCountryId =
        LANGUAGE_CODE_BASED_ON_COUNTRY_ID[countryId];

      handlePrimaryLanguageChange(defaultLanguageBasedOnCountryId, false);
      clearContentVariants({}); // If allowReGeneration = true in handlePrimaryLanguageChange remove this.
      setIsProjectChanged(false);
    }
  };

  const clearCVPAndVerticals = () => {
    // Clearing vertical, if project is changed. Because cvp has dependency with project(cvp is based on country and vertical)
    form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.VERTICAL, undefined);

    setCVPValue('');
  };

  const handleProjectSelect = (selectedProject: string) => {
    if (
      selectedProject === projectValues.currentValue ||
      isCurrentContentSourceManual
    ) {
      return;
    }

    setProjectValue({
      currentValue: selectedProject,
      previousValue: projectValues.currentValue,
    });
    if (lastRoundNumber > 0) {
      setIsProjectSelectionConfirmationOpen(true);
      return;
    }

    clearCVPAndVerticals();
    setIsProjectChanged(true);
  };

  const confirmProjectSelection = () => {
    clearCVPAndVerticals();
    clearContentVariants({});
    setIsProjectChanged(true);
    dispatch(getUCDProject(watchProject));
    setIsProjectSelectionConfirmationOpen(false);
  };

  const revertProjectSelection = () => {
    setIsProjectSelectionConfirmationOpen(false);
    form.setFieldValue(
      SMART_CAMPAIGN_FORM_KEYS.PROJECT,
      projectValues.previousValue,
    );
    setProjectValue({
      currentValue: projectValues.previousValue,
      previousValue: projectValues.currentValue,
    });
  };

  const verticalOptions = useMemo(() => {
    if (!props.configs || !props.configs.verticals) {
      return;
    }

    const verticalsToBeRemoved = ['payment', 'subscription', 'transport2w', 'transport3w', 'transport4w'];
    props.configs.verticals = props.configs.verticals.filter(
      (vertical: string) => !verticalsToBeRemoved.includes(vertical),
    );

    return props.configs.verticals.map((vertical: string) => {
      const cvpOptions = shouldUseContentPortalProducts
        ? getContentPortalCVPOptionsBasedOnVertical({
          contentPortalProducts,
          vertical,
          includeOthers: true,
        })
        : getCVPOptionsBasedOnVerticalsAndCountry({
          countryID,
          vertical,
        });
      return {
        key: vertical,
        label: _.get(VERTICALS_DISPLAY_NAMES, vertical),
        ...(cvpOptions && { children: cvpOptions }),
      };
    });
  }, [
    projectView,
    props.configs,
    shouldUseContentPortalProducts,
    contentPortalProducts,
    contentPortalCvps,
  ]);

  useEffect(() => {
    if (
      !shouldUseContentPortalProducts ||
      !hasLoadedContentPortalProducts ||
      !watchVertical
    ) {
      return;
    }

    const currentCVPValue =
      form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP) || '';
    const nextCVPValue = getContentPortalCVPValueAfterProductsLoad({
      contentPortalProducts,
      vertical: watchVertical,
      cvp: currentCVPValue,
    });

    if (nextCVPValue === currentCVPValue) {
      if (cvpValue !== currentCVPValue) {
        setCVPValue(currentCVPValue);
      }
      return;
    }

    form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP, nextCVPValue);
    setCVPValue(nextCVPValue);
  }, [
    shouldUseContentPortalProducts,
    hasLoadedContentPortalProducts,
    watchVertical,
    contentPortalProducts,
    cvpValue,
    form,
  ]);

  // Implicit Inputs
  const { productOverview, reasonToUse, callToAction } = useMemo(() => {
    const countryID =
      projectView && projectView.project && projectView.project.countryID;

    const metaData = getMetaDataForMystiqueCall({
      countryId: countryID,
      vertical: watchVertical,
      cvp: cvpValue,
      language: selectedLanguages[0], // Primary language
      contentPortalProducts,
      shouldUseContentPortalProducts,
    });

    return (
      metaData || { productOverview: '', reasonToUse: '', callToAction: '' }
    );
  }, [
    watchVertical,
    projectView,
    selectedLanguages,
    cvpValue,
    contentPortalProducts,
    shouldUseContentPortalProducts,
  ]);

  const handleValueChangeInVerticalSelector = ({ keyPath }: { keyPath: string[] }) => {
    if (!keyPath || !keyPath.length) {
      return;
    }

    const path = keyPath.reverse();
    const cvp = getCVPValueFromKey(path[1]);
    const vertical = path[0];

    const cvpOrVerticalChanged = cvp !== cvpValue || vertical !== watchVertical;
    if (
      !isPrimaryLanguageContentsEmpty &&
      !isCurrentContentSourceManual &&
      !isVerticalChangeWarningModalOpen &&
      cvpOrVerticalChanged
    ) {
      setIsVerticalChangeWarningModalOpen(true);
      setPreviousVerticalCVPValues(
        [
          getFormattedCVPKey({ vertical: watchVertical, cvp: cvpValue }),
          watchVertical,
        ].filter(Boolean),
      ); // Setting previous value as same as "keyPath" from selector.
    }

    setCVPValue(cvp);
    form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.VERTICAL, vertical);
    form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP, cvp);

    setShowEditableFormInputs(false);
    insertMystiqueMetaDataToFormItems({
      projectView,
      formInstance: form,
      language: selectedLanguages[0],
      setCallToActionOptions,
      contentPortalProducts,
      shouldUseContentPortalProducts,
    });

    validateSmartCampaignFields({
      form,
      isMystiqueContentEmpty,
      isCVPOthers,
      isEditableInputs: showEditableFormInputs,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded: hasLoadedContentPortalProducts,
      hasContentPortalProductsLoadFailed,
    });
    // // Fix: error message is not getting cleared even if value is selected.
    try {
      form.validateFields([SMART_CAMPAIGN_FORM_KEYS.VERTICAL]);
    } catch (errors) {
      console.log(errors);
    }
  };

  const isCVPOthers = useMemo(() => {
    if (shouldUseContentPortalProducts) {
      return isContentPortalCVPOthersSelected(cvpValue);
    }

    return isCVPOthersSelected(watchVertical, cvpValue);
  }, [watchVertical, cvpValue, shouldUseContentPortalProducts]);

  const formattedVerticalSelectorLabel = useMemo(() => {
    if (!watchVertical) {
      return;
    }

    const verticalDisplayName = _.get(VERTICALS_DISPLAY_NAMES, watchVertical);
    let cvpDisplayName = _.get(CVP_DISPLAY_NAMES, cvpValue || '');
    if (isCVPOthers) {
      cvpDisplayName = OTHERS_OF_CVP;
    } else if (shouldUseContentPortalProducts) {
      cvpDisplayName = getContentPortalProductDisplayLabel({
        products: contentPortalProducts,
        cvps: contentPortalCvps,
        selectedValue: cvpValue,
        language: selectedLanguages[0],
      });
    }

    return `${verticalDisplayName} ${cvpDisplayName ? ` / ${cvpDisplayName}` : ''}`;
  }, [
    watchVertical,
    cvpValue,
    isCVPOthers,
    shouldUseContentPortalProducts,
    contentPortalProducts,
    contentPortalCvps,
    selectedLanguages,
  ]);

  const handleAddNewItemInCallToAction = () => {
    const newCallToAction = form
      .getFieldValue(SMART_CAMPAIGN_FORM_KEYS.NEW_CALL_TO_ACTION)
      .trim();
    if (!newCallToAction) {
      return;
    }

    setCallToActionOptions((prevCTA: CallToActionOption[]) => {
      const options = getCallToActionOptionsFromArray([
        ...prevCTA.map((cta) => cta.value),
        newCallToAction,
      ]);
      return options;
    });
    form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.NEW_CALL_TO_ACTION, '');
  };

  const additionalContentMetaDataOptions = [
    { label: 'Image', key: CONTENT_METADATA_OPTION_KEYS.image },
    { label: 'Key pointers', key: CONTENT_METADATA_OPTION_KEYS.keyPointer },
  ];

  const imageFileSize =
    imageDataForEachLanguage[PN_IMAGE_KEY] &&
    imageDataForEachLanguage[PN_IMAGE_KEY].size;

  const [contentMetaDataState, setContentMetaDataState] = useState({
    showKeyPointer: !!form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.KEY_POINTER),
    showImageField:
      !!form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL) ||
      !!(imageFileSize > SMART_CAMPAIGN_MAX_IMAGE_SIZE * 1000),
    metaDataOptions: [...additionalContentMetaDataOptions],
  });

  useEffect(() => {
    const updatedOptions = [...additionalContentMetaDataOptions].filter(
      (option) => {
        if (option.key === CONTENT_METADATA_OPTION_KEYS.image) {
          return (
            !contentMetaDataState.showImageField &&
            watchChannel === SMART_CAMPAIGN_CHANNELS.push
          );
        } else if (option.key === CONTENT_METADATA_OPTION_KEYS.keyPointer) {
          return !contentMetaDataState.showKeyPointer;
        }
        return false;
      },
    );

    setContentMetaDataState((prev) => ({
      ...prev,
      metaDataOptions: updatedOptions,
    }));
  }, [
    watchChannel,
    contentMetaDataState.showImageField,
    contentMetaDataState.showKeyPointer,
  ]);

  // Watch form values directly instead of using local state
  const isRecurrenceEnabled = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN, form) || false;

  const handleRecurringCampaignChange = (checked: boolean) => {
    if (form) {
      form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN, checked);
      if (checked && !form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.RECURRENCE_WINDOW_DAYS)) {
        form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.RECURRENCE_WINDOW_DAYS, 3);
      }
    }
  };

  return (
    <React.Fragment>
      <Typography.Title style={{ margin: 0, padding: '16px 24px' }} level={4}>
        Input
      </Typography.Title>
      <Divider style={{ marginTop: 0 }} />
      <SectionContainer title={'Context'}>
        <SmartCampaignMetaDataContext
          onProjectSearch={onProjectSearch}
          handleProjectSelect={handleProjectSelect}
          isProjectAndVerticalSelectorDisabled={
            isProjectAndVerticalSelectorDisabled
          }
          projectSuggestions={projectSuggestions}
          handleValueChangeInVerticalSelector={
            handleValueChangeInVerticalSelector
          }
          verticalOptions={verticalOptions}
          watchVertical={watchVertical}
          cvpValue={cvpValue}
          formattedVerticalSelectorLabel={formattedVerticalSelectorLabel}
          projectView={projectView}
          userEmail={userEmail}
          isEditMode={isEditMode}
        />
      </SectionContainer>
      <SectionContainer title="User">
        <SmartCampaignMetaDataUser />
      </SectionContainer>
      <SectionContainer title="Campaign period">
        <SmartCampaignMetadataTime watchProject={watchProject} isRecurrenceEnabled={isRecurrenceEnabled} onRecurringCampaignChange={handleRecurringCampaignChange} />
      </SectionContainer>
      <SectionContainer title="Content">
        <SmartCampaignMetaDataContents
          isCVPOthers={isCVPOthers}
          callToActionOptions={callToActionOptions}
          handleAddNewItemInCallToAction={handleAddNewItemInCallToAction}
          contentMetaDataState={contentMetaDataState}
          watchChannel={watchChannel}
          updateImageData={updateImageData}
          setContentMetaDataState={setContentMetaDataState}
          imageDataForEachLanguage={imageDataForEachLanguage}
        />
      </SectionContainer>
      {watchChannel === SMART_CAMPAIGN_CHANNELS.push && (
        <SectionContainer title="Action">
          <SmartCampaignMetaDataAction
            watchPushChannelAction={watchPushChannelAction}
          />
        </SectionContainer>
      )}
      {!isCurrentContentSourceManual && (
        <SmartCampaignMetaDataImplicitInputs
          reasonToUse={
            !isCVPOthers &&
            (form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.REASON_TO_USE) ||
              reasonToUse)
          }
          callToAction={
            !isCVPOthers &&
            (form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CALL_TO_ACTIONS) ||
              callToAction)
          }
          productOverview={
            !isCVPOthers &&
            (form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PRODUCT_OVERVIEW) ||
              productOverview)
          }
          vertical={watchVertical}
          projectDetails={
            projectView && projectView.projectID === watchProject
              ? projectView
              : undefined
          }
          tone={
            form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.TONE) ||
            CREATE_SMART_CAMPAIGN_DEFAULTS.tone
          }
          isCVPOthers={isCVPOthers}
          callToActionOptions={callToActionOptions}
          handleAddNewItemInCallToAction={handleAddNewItemInCallToAction}
        />
      )}
      <Flex
        justify="flex-end"
        style={{
          padding: '16px 24px',
          bottom: 0,
          zIndex: 10,
          position: 'sticky',
          backgroundColor: colorBaseNeutralWhite,
          boxShadow: `0px 0px 15px 0px ${colorBaseNeutral75}`,
        }}
      >
        {!isCurrentContentSourceManual ? (
          <Button
            type="primary"
            onClick={() => handleGenerateClick({})}
            loading={isMystiqueCallPending}
            disabled={
              lastRoundNumber === 1 &&
              isMystiqueContentEmpty &&
              isCurrentContentSourceManual
            }
          >
            {!isMystiqueContentEmpty ? 'Generate More' : 'Generate'}
            <AiOutlined />
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={generateManualModeContents}
            disabled={
              isCurrentContentSourceManual && !isPrimaryLanguageContentsEmpty
            }
          >
            Next
          </Button>
        )}
      </Flex>

      <Modal
        centered
        width={440}
        okText="Continue"
        cancelText="Back"
        onOk={confirmProjectSelection}
        onCancel={revertProjectSelection}
        okButtonProps={{ type: 'secondary' }}
        styles={{ body: { paddingRight: 10 } }}
        cancelButtonProps={{ type: 'primary' }}
        open={isProjectSelectionConfirmationOpen}
        title="Changing project will affect current content"
      >
        <Typography.Body level={3}>
          The project you selected is tied to a specific objective and language.
          Changing the project will discard the current content.
        </Typography.Body>
      </Modal>

      <Modal
        centered
        width={440}
        okText="Continue"
        cancelText="Back"
        onOk={() => {
          clearContentVariants({ shouldClearSlangToggle: false });
          setIsVerticalChangeWarningModalOpen(false);
          handleGenerateClick({
            languageToGenerateContents: selectedLanguages[0],
          });
          setPrimaryLanguageContentsInForm((prev: PrimaryLanguageContents) => ({
            ...prev,
            mode: SMART_CAMPAIGN_CONTENT_SOURCE_MODES.automatic,
          }));
        }}
        onCancel={() => {
          handleValueChangeInVerticalSelector({
            keyPath: previousVerticalCVPValues,
          });
          setIsVerticalChangeWarningModalOpen(false);
        }}
        okButtonProps={{ type: 'secondary' }}
        styles={{ body: { paddingRight: 10 } }}
        cancelButtonProps={{ type: 'primary' }}
        open={isVerticalChangeWarningModalOpen}
        title="Changing vertical will affect current content"
      >
        <Typography.Body level={3}>
          We generate content based on the selected vertical. Changing the
          vertical will discard the current content
        </Typography.Body>
      </Modal>
    </React.Fragment>
  );
};

const SectionContainer = (props: {
  children: React.ReactNode;
  title: string;
}) => {
  return (
    <React.Fragment>
      <Typography.Title
        level={5}
        style={{ padding: '0px 24px', marginBottom: 24 }}
      >
        {props.title}
      </Typography.Title>
      <div style={{ padding: '0px 24px' }}>{props.children}</div>
      <Divider />
    </React.Fragment>
  );
};

export default SmartCampaignMetaDataForm;
